import { useEffect, useRef, useCallback } from 'react'
import { initSocket, getSocket, disconnectSocket } from '../services/videocall/socket'

/**
 * Custom hook for Socket.io management
 * @returns {Object} Socket instance and utility functions
 */
export function useSocket() {
  const socketRef = useRef(null)
  const listenersRef = useRef(new Map())

  // Initialize socket
  useEffect(() => {
    socketRef.current = initSocket()

    return () => {
      // Cleanup all listeners
      listenersRef.current.forEach((handler, event) => {
        socketRef.current?.off(event, handler)
      })
      listenersRef.current.clear()
    }
  }, [])

  /**
   * Subscribe to socket event
   */
  const on = useCallback((event, handler) => {
    const socket = getSocket()
    if (socket) {
      socket.on(event, handler)
      listenersRef.current.set(event, handler)
    }
  }, [])

  /**
   * Unsubscribe from socket event
   */
  const off = useCallback((event) => {
    const socket = getSocket()
    const handler = listenersRef.current.get(event)
    if (socket && handler) {
      socket.off(event, handler)
      listenersRef.current.delete(event)
    }
  }, [])

  /**
   * Emit socket event
   */
  const emit = useCallback((event, data) => {
    const socket = getSocket()
    if (socket) {
      socket.emit(event, data)
    }
  }, [])

  /**
   * Get socket ID
   */
  const getSocketId = useCallback(() => {
    return getSocket()?.id
  }, [])

  /**
   * Check if socket is connected
   */
  const isConnected = useCallback(() => {
    return getSocket()?.connected || false
  }, [])

  return {
    socket: socketRef.current,
    on,
    off,
    emit,
    getSocketId,
    isConnected,
    disconnect: disconnectSocket,
  }
}
